import { COUNTRY_CONFIG } from "../utils";
import { BarDataItem, HolidayCountryType } from "../types";

interface ScaleFn {
  (value: string): number | undefined;
  bandwidth?: () => number;
}

interface AxisMap {
  scale: ScaleFn;
  y?: number;
  height?: number;
}

interface RechartsCustomizedProps {
  xAxisMap?: Record<string, AxisMap>;
  yAxisMap?: Record<string, AxisMap>;
  data?: BarDataItem[];
  [key: string]: unknown;
}

const HATCH_FILL: Record<HolidayCountryType, string> = {
  [HolidayCountryType.NONE]: "",
  [HolidayCountryType.ROMANIA]: "url(#hatch-ro)",
  [HolidayCountryType.GEORGIA]: "url(#hatch-ge)",
  [HolidayCountryType.BOTH]: "url(#hatch-both)",
};

const PAT_SIZE = 6;

const HolidayOverlay = ({
  xAxisMap,
  yAxisMap,
  data,
}: RechartsCustomizedProps) => {
  if (!xAxisMap || !yAxisMap || !data) return null;

  const xAxis = Object.values(xAxisMap)[0];
  const yAxis = Object.values(yAxisMap)[0];
  if (!xAxis?.scale || !yAxis) return null;

  const scale = xAxis.scale;
  const bandwidth: number = scale.bandwidth ? scale.bandwidth() : 0;
  const chartTop: number = yAxis.y ?? 0;
  const chartHeight: number = yAxis.height ?? 0;

  return (
    <g>
      <defs>
        <pattern
          id="hatch-ro"
          patternUnits="userSpaceOnUse"
          width={PAT_SIZE}
          height={PAT_SIZE}
          patternTransform="rotate(45)"
        >
          <rect
            width={PAT_SIZE}
            height={PAT_SIZE}
            fill={COUNTRY_CONFIG.RO.color + "26"}
          />
          <line
            x1={0}
            y1={0}
            x2={0}
            y2={PAT_SIZE}
            stroke={COUNTRY_CONFIG.RO.color}
            strokeWidth={2}
          />
        </pattern>
        <pattern
          id="hatch-ge"
          patternUnits="userSpaceOnUse"
          width={PAT_SIZE}
          height={PAT_SIZE}
          patternTransform="rotate(45)"
        >
          <rect
            width={PAT_SIZE}
            height={PAT_SIZE}
            fill={COUNTRY_CONFIG.GE.color + "26"}
          />
          <line
            x1={0}
            y1={0}
            x2={0}
            y2={PAT_SIZE}
            stroke={COUNTRY_CONFIG.GE.color}
            strokeWidth={2}
          />
        </pattern>
        <pattern
          id="hatch-both"
          patternUnits="userSpaceOnUse"
          width={PAT_SIZE * 2}
          height={PAT_SIZE * 2}
          patternTransform="rotate(45)"
        >
          <rect
            width={PAT_SIZE * 2}
            height={PAT_SIZE * 2}
            fill={COUNTRY_CONFIG.RO.color + "1a"}
          />
          <line
            x1={0}
            y1={0}
            x2={0}
            y2={PAT_SIZE * 2}
            stroke={COUNTRY_CONFIG.RO.color}
            strokeWidth={2}
          />
          <line
            x1={PAT_SIZE}
            y1={0}
            x2={PAT_SIZE}
            y2={PAT_SIZE * 2}
            stroke={COUNTRY_CONFIG.GE.color}
            strokeWidth={2}
          />
        </pattern>
      </defs>
      {(data as BarDataItem[]).map((entry, i) => {
        const ht = entry.holidayType as HolidayCountryType | undefined;
        if (ht === undefined || ht === HolidayCountryType.NONE) return null;
        const x = scale(entry.date);
        if (x == null) return null;
        return (
          <rect
            key={i}
            x={x}
            y={chartTop}
            width={bandwidth}
            height={chartHeight}
            fill={HATCH_FILL[ht]}
            style={{ pointerEvents: "none" }}
          />
        );
      })}
    </g>
  );
};

export default HolidayOverlay;
